import { createContext, useCallback, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { secondaryBlue } from '../styles/palette'

type ThemeMode = 'light' | 'dark'

interface ThemeContextValue {
  mode: ThemeMode
  toggleTheme: () => void
}

export const ThemeContext = createContext<ThemeContextValue | null>(null)

const buildTheme = (mode: ThemeMode) =>
  createTheme({
    palette: {
      mode,
      primary: { main: secondaryBlue[500] },
      secondary: { main: secondaryBlue[500] },
      error: { main: '#ef4444' },
      warning: { main: '#f59e0b' },
      success: { main: '#22c55e' },
      background: mode === 'dark' ? { default: '#0d0f14', paper: '#161920' } : { default: '#f5f5f5', paper: '#ffffff' },
      text:
        mode === 'dark' ? { primary: '#e8eaf0', secondary: '#8b90a0' } : { primary: '#111827', secondary: '#6b7280' },
      divider: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.12)',
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: 13,
    },
    shape: { borderRadius: 8 },
    components: {
      MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
      MuiButton: { styleOverrides: { root: { textTransform: 'none', fontWeight: 500 } } },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: 13,
            '&:hover': {
              backgroundColor: secondaryBlue[50],
            },
            '&.Mui-selected': {
              backgroundColor: secondaryBlue[50],
              color: secondaryBlue[700],
              fontWeight: 600,
              '&:hover': {
                backgroundColor: secondaryBlue[50],
              },
            },
          },
        },
      },
    },
  })

export const ThemeProvider = ({ children }: { children: ReactNode }): React.JSX.Element => {
  const [mode, setMode] = useState<ThemeMode>('light')

  const toggleTheme = useCallback((): void => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'))
  }, [])

  const muiTheme = useMemo(() => buildTheme(mode), [mode])
  const value = useMemo<ThemeContextValue>(() => ({ mode, toggleTheme }), [mode, toggleTheme])

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  )
}
